// @ts-nocheck
import { useEffect, useState, useCallback } from "react";

const RESET_TIMEOUT = 60000; // 60 seconds inactivity reset

async function api(path: string, init: RequestInit = {}) {
  const slug = window.location.hostname.split(".")[0];
  const r = await fetch(path, { ...init, headers: { "x-tenant-slug": slug, ...(init.headers || {}) } });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body.error || "Error");
  return body;
}

function pounds(pence: number) {
  return `£${(pence / 100).toFixed(2)}`;
}

type Step = "welcome" | "service" | "staff" | "slot" | "details" | "confirm" | "done";

function ExitButton({ authMode }: { authMode: string }) {
  const [show, setShow] = useState(false);
  const [pin, setPin] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function attempt(p?: string) {
    setBusy(true); setError("");
    try {
      const body = authMode === "MANAGER_PIN" ? { pin: p ?? pin } : { email, password };
      await api("/api/public/appointments/kiosk-auth", {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      });
      window.location.href = "/";
    } catch {
      setError(authMode === "MANAGER_PIN" ? "Incorrect PIN." : "Invalid email or password.");
      setPin(""); setBusy(false);
    }
  }

  function addDigit(d: string) {
    if (pin.length >= 6) return;
    const next = pin + d;
    setPin(next);
    if (next.length >= 4) attempt(next);
  }

  if (!show) return (
    <button className="kiosk-exit-btn" onClick={() => setShow(true)} title="Staff access">⚙</button>
  );

  return (
    <div className="kiosk-exit-overlay" onClick={e => e.target === e.currentTarget && setShow(false)}>
      <div className="kiosk-exit-dialog">
        <h3>Staff access</h3>
        {authMode === "MANAGER_PIN" ? (
          <>
            <div className="kiosk-pin-dots">
              {[0,1,2,3,4,5].map(i => <div key={i} className={`kiosk-pin-dot ${i < pin.length ? "filled" : ""}`} />)}
            </div>
            {error && <p className="kiosk-error-msg">{error}</p>}
            <div className="kiosk-numpad">
              {["1","2","3","4","5","6","7","8","9","","0","⌫"].map((d, i) => (
                <button key={i} className={`kiosk-numpad-key ${!d ? "kiosk-numpad-empty" : ""}`}
                  onClick={() => d === "⌫" ? setPin(p => p.slice(0,-1)) : d && addDigit(d)}>
                  {d}
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <input className="kiosk-input" type="email" placeholder="Email address" value={email} onChange={e => setEmail(e.target.value)} />
            <input className="kiosk-input" type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === "Enter" && attempt()} />
            {error && <p className="kiosk-error-msg">{error}</p>}
            <button className="kiosk-cta" style={{fontSize:18,padding:"14px 24px"}} disabled={busy || !email || !password} onClick={() => attempt()}>
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </>
        )}
        <button className="kiosk-btn-secondary" style={{marginTop:8}} onClick={() => { setShow(false); setPin(""); setError(""); }}>Cancel</button>
      </div>
    </div>
  );
}

export function KioskPage() {

  const [catalog, setCatalog] = useState<any>(null);
  const [authMode, setAuthMode] = useState("FULL_LOGIN");
  const [step, setStep] = useState<Step>("welcome");
  const [service, setService] = useState<any>(null);
  const [staff, setStaff] = useState<any>(null);
  const [slot, setSlot] = useState<any>(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [slots, setSlots] = useState<any[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [booking, setBooking] = useState<any>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetTimer, setResetTimer] = useState<any>(null);
  const [countdown, setCountdown] = useState(0);

  function reset() {
    setStep("welcome");
    setService(null); setStaff(null); setSlot(null);
    setName(""); setPhone(""); setSlots([]);
    setBooking(null); setError("");
    if (resetTimer) clearTimeout(resetTimer);
  }

  // Reset on inactivity
  const resetActivity = useCallback(() => {
    if (resetTimer) clearTimeout(resetTimer);
    if (step === "done" || step === "welcome") return;
    const t = setTimeout(() => reset(), RESET_TIMEOUT);
    setResetTimer(t);
  }, [step, resetTimer]);

  useEffect(() => {
    const events = ["touchstart", "click", "keydown"];
    events.forEach(e => window.addEventListener(e, resetActivity, { passive: true }));
    return () => events.forEach(e => window.removeEventListener(e, resetActivity));
  }, [resetActivity]);

  // Auto-reset after done screen
  useEffect(() => {
    if (step === "done") {
      let secs = 8;
      setCountdown(secs);
      const interval = setInterval(() => {
        secs--;
        setCountdown(secs);
        if (secs <= 0) { clearInterval(interval); reset(); }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [step]);

  // Load catalog and kiosk settings
  useEffect(() => {
    Promise.all([
      api("/api/public/appointments/catalog"),
      api("/api/public/appointments/kiosk-settings").catch(() => ({ authMode: "FULL_LOGIN" })),
    ]).then(([cat, ks]) => {
      setCatalog(cat);
      setAuthMode(ks.authMode || "FULL_LOGIN");
    }).catch(() => setError("Unable to load booking information. Please ask a member of staff."));
  }, []);

  async function loadSlots(serviceId: string, staffId?: string) {
    setSlotsLoading(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const params = new URLSearchParams({ serviceId, date: today, walkIn: "true" });
      if (staffId) params.set("staffId", staffId);
      const data = await api(`/api/public/appointments/slots?${params}`);
      // Only show slots from now onwards
      const now = new Date();
      setSlots((data.slots || []).filter((s: any) => new Date(s.startsAt) > now));
    } catch { setSlots([]); }
    finally { setSlotsLoading(false); }
  }

  async function book() {
    setBusy(true); setError("");
    try {
      const result = await api("/api/public/appointments/book", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          serviceId: service.id,
          staffId: slot.staffId,
          startsAt: slot.startsAt,
          customerName: name.trim(),
          customerPhone: phone.trim() || undefined,
          source: "WALK_IN",
          paymentChoice: "PAY_ON_DAY",
        }),
      });
      setBooking(result);
      setStep("done");
    } catch (e: any) {
      setError(e.message.replaceAll("_", " "));
    } finally { setBusy(false); }
  }

  const branding = catalog?.branding ?? {};
  const primary = branding.primary_color || "#102a43";
  const accent = branding.accent_color || "#12a594";
  const availableServices = (catalog?.services || []).filter((s: any) => !s.blocked);

  if (error && !catalog) return (
    <div className="kiosk-error">
      <p>{error}</p>
    </div>
  );

  if (!catalog) return (
    <div className="kiosk-loading" style={{ background: primary }}>
      <div className="kiosk-spinner" />
      <p>Loading…</p>
    </div>
  );

  return (
    <div className="kiosk-shell" style={{ "--kiosk-primary": primary, "--kiosk-accent": accent } as any}>
      {/* Header */}
      <header className="kiosk-header" style={{ background: primary }}>
        {branding.logo_url && <img src={branding.logo_url} alt={catalog.company} className="kiosk-logo" />}
        <span className="kiosk-company">{catalog.company}</span>
        {step !== "welcome" && step !== "done" && (
          <button className="kiosk-back" onClick={reset}>✕ Cancel</button>
        )}
      </header>

      <main className="kiosk-main">

        {/* Welcome */}
        {step === "welcome" && (
          <div className="kiosk-welcome">
            <h1>Welcome!</h1>
            <p>{branding.welcome_message || "Book your appointment below."}</p>
            <button className="kiosk-cta" style={{ background: accent }} onClick={() => setStep("service")}>
              Book a walk-in appointment
            </button>
            <p className="kiosk-sub">Touch to get started</p>
          </div>
        )}

        {/* Service selection */}
        {step === "service" && (
          <div className="kiosk-step">
            <h2>What service would you like?</h2>
            <div className="kiosk-grid">
              {availableServices.map((s: any) => (
                <button key={s.id} className="kiosk-card" onClick={() => {
                  setService(s); setStep("staff");
                }}>
                  <b>{s.name}</b>
                  <span>{s.duration_minutes} min</span>
                  <span className="kiosk-price">{pounds(s.price_pence)}</span>
                  {s.description && <small>{s.description}</small>}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Staff selection */}
        {step === "staff" && service && (
          <div className="kiosk-step">
            <h2>Who would you like?</h2>
            <div className="kiosk-grid">
              <button className="kiosk-card kiosk-any" onClick={async () => {
                setStaff(null); await loadSlots(service.id); setStep("slot");
              }}>
                <b>Anyone available</b>
                <small>We'll find the next available appointment</small>
              </button>
              {(catalog.staff || [])
                .filter((s: any) => (catalog.assignments || []).some((a: any) => a.staff_id === s.id && a.service_id === service.id))
                .map((s: any) => (
                  <button key={s.id} className="kiosk-card" onClick={async () => {
                    setStaff(s); await loadSlots(service.id, s.id); setStep("slot");
                  }}>
                    {s.photo_url && <img src={s.photo_url} alt={s.name} className="kiosk-staff-photo" />}
                    <b>{s.name}</b>
                    {s.job_title && <small>{s.job_title}</small>}
                  </button>
                ))}
            </div>
          </div>
        )}

        {/* Slot selection */}
        {step === "slot" && (
          <div className="kiosk-step">
            <h2>When would you like to come in?</h2>
            {slotsLoading ? <div className="kiosk-spinner" /> : slots.length === 0 ? (
              <div className="kiosk-empty">
                <p>No slots available today{staff ? ` with ${staff.name}` : ""}.</p>
                <button className="kiosk-btn-secondary" onClick={() => setStep("staff")}>Choose someone else</button>
              </div>
            ) : (
              <div className="kiosk-slots">
                {slots.slice(0, 12).map((s: any, i: number) => (
                  <button key={i} className="kiosk-slot" onClick={() => { setSlot(s); setStep("details"); }}>
                    <b>{new Date(s.startsAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</b>
                    {s.staffName && <small>{s.staffName}</small>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Customer details */}
        {step === "details" && slot && (
          <div className="kiosk-step kiosk-form">
            <h2>Just a few details</h2>
            <label>Your name <span className="kiosk-required">*</span>
              <input autoFocus value={name} onChange={e => setName(e.target.value)}
                placeholder="First and last name" className="kiosk-input" />
            </label>
            <label>Mobile number <span className="kiosk-optional">(optional)</span>
              <input type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                placeholder="+44 7700 900000" className="kiosk-input" />
            </label>
            <button className="kiosk-cta" style={{ background: accent }}
              disabled={!name.trim()}
              onClick={() => setStep("confirm")}>
              Continue
            </button>
          </div>
        )}

        {/* Confirm */}
        {step === "confirm" && slot && service && (
          <div className="kiosk-step kiosk-confirm">
            <h2>Confirm your booking</h2>
            <div className="kiosk-summary">
              <div><span>Service</span><b>{service.name}</b></div>
              <div><span>Time</span><b>{new Date(slot.startsAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })} today</b></div>
              {slot.staffName && <div><span>With</span><b>{slot.staffName}</b></div>}
              <div><span>Price</span><b>{pounds(service.price_pence)}</b></div>
              <div><span>Name</span><b>{name}</b></div>
              {phone && <div><span>Mobile</span><b>{phone}</b></div>}
            </div>
            {error && <p className="kiosk-error-msg">{error}</p>}
            <button className="kiosk-cta" style={{ background: accent }} disabled={busy} onClick={book}>
              {busy ? "Booking…" : "Confirm booking"}
            </button>
            <button className="kiosk-btn-secondary" onClick={() => setStep("details")}>Go back</button>
          </div>
        )}

        {/* Done */}
        {step === "done" && booking && (
          <div className="kiosk-done">
            <div className="kiosk-tick" style={{ color: accent }}>✓</div>
            <h1>You're booked in!</h1>
            <p>Reference: <b>{booking.reference}</b></p>
            <p>A member of staff will be with you shortly.</p>
            <p className="kiosk-reset-note">This screen will reset in {countdown} seconds</p>
          </div>
        )}
      </main>

      {/* Step indicator */}
      {step !== "welcome" && step !== "done" && (
        <div className="kiosk-steps">
          {["service","staff","slot","details","confirm"].map((s, i) => (
            <div key={s} className={`kiosk-step-dot ${step === s ? "active" : ["service","staff","slot","details","confirm"].indexOf(step) > i ? "done" : ""}`} />
          ))}
        </div>
      )}
      <ExitButton authMode={authMode} />
    </div>
  );
}
